import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ISO6391 from "iso-639-1";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import TranslateIcon from "@mui/icons-material/Translate";
import PublicIcon from "@mui/icons-material/Public";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import {
  CustomInput,
  CustomTextArea,
} from "../../../components/Common/CustomInputs";
import { CustomDropdown } from "../../../components/Common/CustomDropdown";
import CustomTagInput from "../../../components/Common/CustomTagInput";
import FileUploadButton from "../../../utils/FileUploadButton";
import UploadedFileNote from "../../../utils/UploadedFileNote";
import { getInitials } from "../../../components/ui/Gapper";
import { colors, fonts } from "../../../theme/theme";

import AvatarEditorModal from "../MyProfile/AvatarEditorModal";
import {
  GENDER_OPTIONS,
  COMPLIANCE_DOCS,
} from "../MyProfile/MyProfile.constants";
import {
  SectionCard,
  SectionTitle,
  FieldGrid,
  FullRow,
  AvatarRow,
  ProfileAvatar,
  AvatarName,
  AvatarSubtitle,
  CheckboxRow,
  StatusPill,
  FieldLabel,
  InputFieldLabel,
  phoneInputStyle,
  phoneButtonStyle,
  AvatarWrap,
  AvatarEditButton,
  DocRow,
  DocHeader,
  DocControls,
  HelperText,
} from "../MyProfile/MyProfile.styles";
import { usePractitionerDetail } from "./PractitionerDetailModal.hooks";

const LANGUAGE_OPTIONS = ISO6391.getAllNames();

const PractitionerDetailModal = ({
  practitionerId,
  open,
  onClose,
  onModerate,
  onSaved,
}) => {
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const notify = useCallback(
    (severity, msg) => setSnack({ open: true, severity, msg }),
    [],
  );
  const closeSnack = useCallback(
    () => setSnack((s) => ({ ...s, open: false })),
    [],
  );

  const {
    loading,
    saving,
    uploading,
    form,
    setField,
    handleSingleUpload,
    handleMultiUpload,
    handleRemoveFile,
    handleSave,
  } = usePractitionerDetail(practitionerId, open, notify);

  const doSave = async () => {
    const ok = await handleSave();
    if (ok) {
      onSaved?.(practitionerId);
      onClose();
    }
  };

  const doModerate = (status) => {
    onModerate?.(practitionerId, status);
    onClose();
  };

  const fullName = form ? `${form.firstName} ${form.lastName}`.trim() : "";
  const subtitle = form
    ? (form.specialities || []).slice(0, 2).join(" · ")
    : "";

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontFamily: fonts.gotham, color: colors.primary }}>
        Practitioner Profile
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: colors.headerBg }}>
        {loading || !form ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: colors.secondary }} />
          </Box>
        ) : (
          <>
            {/* Identity + avatar (click to view / reposition / replace) */}
            <SectionCard>
              <AvatarRow>
                <AvatarWrap>
                  <ProfileAvatar
                    src={form.avatar || undefined}
                    onClick={() => setAvatarModalOpen(true)}
                    sx={{ cursor: "pointer" }}
                    imgProps={{
                      style: {
                        objectPosition: form.avatarPosition || "center",
                      },
                    }}
                  >
                    {uploading.avatar ? (
                      <CircularProgress
                        size={20}
                        sx={{ color: colors.white }}
                      />
                    ) : (
                      getInitials(fullName)
                    )}
                  </ProfileAvatar>
                  <AvatarEditButton
                    type="button"
                    onClick={() => setAvatarModalOpen(true)}
                    aria-label="Edit avatar"
                  >
                    <PhotoCameraIcon />
                  </AvatarEditButton>
                </AvatarWrap>
                <div>
                  <AvatarName>{fullName || "Practitioner"}</AvatarName>
                  <AvatarSubtitle>
                    {form.email}
                    {subtitle ? ` · ${subtitle}` : ""}
                  </AvatarSubtitle>
                </div>
              </AvatarRow>
            </SectionCard>

            <AvatarEditorModal
              open={avatarModalOpen}
              onClose={() => setAvatarModalOpen(false)}
              avatar={form.avatar}
              position={form.avatarPosition}
              uploading={!!uploading.avatar}
              onReplace={(file) => handleSingleUpload("avatar", file)}
              onPositionChange={(pos) => setField("avatarPosition", pos)}
            />

            {/* Personal */}
            <SectionCard>
              <SectionTitle>
                <PersonOutlineIcon fontSize="small" /> Personal Information
              </SectionTitle>
              <FieldGrid>
                <CustomInput
                  label="First Name"
                  required
                  value={form.firstName}
                  setValue={(v) => setField("firstName", v)}
                  noIcon
                />
                <CustomInput
                  label="Last Name"
                  required
                  value={form.lastName}
                  setValue={(v) => setField("lastName", v)}
                  noIcon
                />
                <CustomInput
                  label="Email"
                  value={form.email}
                  setValue={() => {}}
                  noIcon
                />
                <div>
                  <InputFieldLabel>Phone</InputFieldLabel>
                  <PhoneInput
                    country="us"
                    value={(form.phone || "").replace(/^\+/, "")}
                    onChange={(v) => setField("phone", v ? `+${v}` : "")}
                    enableSearch
                    countryCodeEditable
                    specialLabel=""
                    containerStyle={{ width: "100%" }}
                    inputStyle={phoneInputStyle}
                    buttonStyle={phoneButtonStyle}
                    dropdownStyle={{ fontFamily: "'Quicksand', sans-serif" }}
                  />
                </div>
                <CustomDropdown
                  label="Gender"
                  value={form.gender}
                  setValue={(v) => setField("gender", v)}
                  data={GENDER_OPTIONS}
                  placeholder="Select"
                  noIcon
                />
                <CustomInput
                  label="Time Zone"
                  value={form.timeZone}
                  setValue={(v) => setField("timeZone", v)}
                  noIcon
                />
                <CustomInput
                  label="City"
                  value={form.city}
                  setValue={(v) => setField("city", v)}
                  noIcon
                />
                <CustomInput
                  label="Country"
                  value={form.country}
                  setValue={(v) => setField("country", v)}
                  noIcon
                />
                <FullRow>
                  <CustomTextArea
                    label="Bio"
                    value={form.bio}
                    setValue={(v) => setField("bio", v)}
                    rows={10}
                    noIcon
                  />
                </FullRow>
              </FieldGrid>
            </SectionCard>

            {/* Professional */}
            <SectionCard>
              <SectionTitle>
                <WorkOutlineIcon fontSize="small" /> Professional Details
              </SectionTitle>
              <FieldGrid>
                <FullRow>
                  <CustomTagInput
                    label="Specialities"
                    hashTags={form.specialities}
                    setHashTags={(v) => setField("specialities", v)}
                    placeholder="Add a speciality and press Enter"
                  />
                </FullRow>
                <CustomInput
                  label="Hourly Rate (USD)"
                  type="number"
                  value={form.hourlyRate}
                  setValue={(v) => setField("hourlyRate", v)}
                  noIcon
                />
                <div>
                  <FieldLabel>Moderation Status</FieldLabel>
                  <StatusPill $approved={form.moderationStatus === "approved"}>
                    {form.moderationStatus === "approved"
                      ? "Approved"
                      : form.moderationStatus === "rejected"
                        ? "Rejected"
                        : "Pending Review"}
                  </StatusPill>
                </div>
                <FullRow>
                  <CheckboxRow>
                    <Checkbox
                      checked={!!form.licensed}
                      onChange={(e) => setField("licensed", e.target.checked)}
                      sx={{
                        color: colors.secondary,
                        "&.Mui-checked": { color: colors.secondary },
                      }}
                    />
                    Holds a valid professional license
                  </CheckboxRow>
                </FullRow>
                <FullRow>
                  <CustomTextArea
                    label="Experience (one entry per line)"
                    value={form.experience}
                    setValue={(v) => setField("experience", v)}
                    rows={4}
                    noIcon
                  />
                </FullRow>
                <FullRow>
                  <CustomTextArea
                    label="Education (one entry per line)"
                    value={form.education}
                    setValue={(v) => setField("education", v)}
                    rows={4}
                    noIcon
                  />
                </FullRow>
                <FullRow>
                  <CustomTextArea
                    label="Certifications (one entry per line)"
                    value={form.certifications}
                    setValue={(v) => setField("certifications", v)}
                    rows={4}
                    noIcon
                  />
                </FullRow>
              </FieldGrid>
            </SectionCard>

            {/* Languages */}
            <SectionCard>
              <SectionTitle>
                <TranslateIcon fontSize="small" /> Languages
              </SectionTitle>
              <FieldLabel>Languages Spoken</FieldLabel>
              <Autocomplete
                multiple
                options={LANGUAGE_OPTIONS}
                value={Array.isArray(form.languages) ? form.languages : []}
                onChange={(e, v) => setField("languages", v)}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select languages"
                    size="small"
                  />
                )}
              />
            </SectionCard>

            {/* Social & Web */}
            <SectionCard>
              <SectionTitle>
                <PublicIcon fontSize="small" /> Social & Web Presence
              </SectionTitle>
              <FieldGrid>
                <CustomInput
                  label="Instagram"
                  value={form.instagram}
                  setValue={(v) => setField("instagram", v)}
                  placeholder="https://instagram.com/yourprofile"
                  Icon={InstagramIcon}
                  iconColor="#E4405F"
                />
                <CustomInput
                  label="Facebook"
                  value={form.fb}
                  setValue={(v) => setField("fb", v)}
                  placeholder="https://facebook.com/yourprofile"
                  Icon={FacebookIcon}
                  iconColor="#1877F2"
                />
                <CustomInput
                  label="LinkedIn"
                  value={form.linkedIn}
                  setValue={(v) => setField("linkedIn", v)}
                  placeholder="https://linkedin.com/in/yourprofile"
                  Icon={LinkedInIcon}
                  iconColor="#0A66C2"
                />
                <CustomInput
                  label="Website"
                  value={form.website}
                  setValue={(v) => setField("website", v)}
                  placeholder="https://yoursite.com"
                  Icon={LanguageIcon}
                  iconColor={colors.secondary}
                />
                <FullRow>
                  <CustomInput
                    label="SimplePractice Booking Link"
                    value={form.simplePracticeBookingUrl}
                    setValue={(v) => setField("simplePracticeBookingUrl", v)}
                    placeholder="https://..."
                    Icon={EventAvailableIcon}
                    iconColor={colors.secondary}
                  />
                </FullRow>
              </FieldGrid>
            </SectionCard>

            {/* Compliance documents — upload / view / delete */}
            <SectionCard>
              <SectionTitle>
                <DescriptionOutlinedIcon fontSize="small" /> Compliance
                Documents
              </SectionTitle>
              <HelperText sx={{ mb: 2 }}>
                Hidden from the public site. Upload PDF or image files.
              </HelperText>
              {COMPLIANCE_DOCS.map((doc) => {
                const value = form[doc.key];
                const uploaded = doc.multi ? (value || []).length > 0 : !!value;
                const busy = !!uploading[doc.key];
                const inputId = `admin-doc-${doc.key}`;
                return (
                  <DocRow key={doc.key}>
                    <DocHeader>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontFamily: "inherit",
                        }}
                      >
                        {uploaded ? (
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: colors.success }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            fontSize="small"
                            sx={{ color: colors.lightBorder }}
                          />
                        )}
                        {doc.label}
                        {doc.multi && (
                          <HelperText>(multiple allowed)</HelperText>
                        )}
                      </span>
                      <StatusPill $approved={uploaded}>
                        {uploaded ? "Complete" : "Pending"}
                      </StatusPill>
                    </DocHeader>
                    <DocControls>
                      <FileUploadButton
                        id={inputId}
                        label={
                          busy
                            ? "Uploading..."
                            : uploaded && !doc.multi
                              ? "Replace"
                              : "Upload"
                        }
                        accept={doc.accept}
                        multiple={doc.multi}
                        onChange={(e) => {
                          if (doc.multi)
                            handleMultiUpload(doc.key, e.target.files);
                          else handleSingleUpload(doc.key, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                      {busy && (
                        <CircularProgress
                          size={18}
                          sx={{ color: colors.secondary }}
                        />
                      )}
                    </DocControls>
                    <UploadedFileNote
                      file={value}
                      onDelete={(f, idx) =>
                        handleRemoveFile(doc.key, doc.multi ? idx : undefined)
                      }
                    />
                  </DocRow>
                );
              })}
            </SectionCard>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => doModerate("approved")}
            sx={{
              textTransform: "none",
              fontFamily: fonts.quicksand,
              color: "#409d45",
            }}
          >
            Approve
          </Button>
          <Button
            size="small"
            startIcon={<AccessTimeIcon />}
            onClick={() => doModerate("pending")}
            sx={{
              textTransform: "none",
              fontFamily: fonts.quicksand,
              color: "#C77A22",
            }}
          >
            Pending
          </Button>
          <Button
            size="small"
            startIcon={<HighlightOffIcon />}
            onClick={() => doModerate("rejected")}
            sx={{
              textTransform: "none",
              fontFamily: fonts.quicksand,
              color: "#C0392B",
            }}
          >
            Reject
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontFamily: fonts.quicksand,
              borderColor: colors.lightBorder,
              color: colors.text,
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={doSave}
            disabled={saving || loading}
            sx={{
              textTransform: "none",
              fontFamily: fonts.quicksand,
              backgroundColor: colors.secondary,
              "&:hover": { backgroundColor: "#1f2e3e" },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default PractitionerDetailModal;
